import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Company } from '../company-settings/shared/models/Company';
import { CompanyManagementService } from '../company-settings/shared/services/companyManagement.service';
import { ActivatedRoute, Params } from '@angular/router';
import { SpinnerService } from 'src/core/services/spinner.service';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { NotificationService } from 'src/app/search-assign/shared/services/notification.service';
import { RightsholderBoardService } from 'src/core/services/rightsholder-board.service';
import { Boards } from 'src/core/helpers/boards';

@Component({
  selector: 'app-super-admin-settings',
  templateUrl: './super-admin-settings.component.html',
  styleUrls: ['./super-admin-settings.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class SuperAdminSettingsComponent implements OnInit {
  companyInfo: Company = {}; 
  paramsSubscription!: Subscription;
  companyForm: FormGroup;
  animationState: string = '';
  parentLabels: any[] = [];
  Denmark: boolean = false;
  Norway: boolean = false;
  currentUser: any;
  boardId!: string;
  organizationId: number | null = localStorage.getItem('organizationId') ? Number(localStorage.getItem('organizationId')) : null;
  isDefaultBoard: boolean = false;

  constructor(
    private route: ActivatedRoute, 
    private messageService: MessageService,
    private spinnerService: SpinnerService,
    private fb: FormBuilder,
    private companyService: CompanyManagementService, 
    private notificationService: NotificationService,
    private rightsholderBoardService: RightsholderBoardService
  ) {
    this.companyForm = this.fb.group({
      memberType: ['', Validators.required],
      labelType: ['', Validators.required],
      isActive: [true, Validators.required],
      collectDirectly: [true],
      setParentLabel: [false],
      collectorId: [''],
      parentBoardId: [''],
      useNemkonto: [false],
      dK_RegNo: [''],
      dK_AccountNo: [''],
      nO_AccountNo: [''],
      intl_NameOfBank: [''],
      intl_IBAN: [''],
      intl_Swicfbic: [''],
      ifpiDanmark: [false],
      ifpiNorge: [false],
      fono: [false],
      companyAccessLevel: [false]
    });
  }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    
    // First, get the boardId from route params
    this.paramsSubscription = this.route.params.subscribe((params: Params) => {
      this.boardId = params['id'] || null;
      // Load parent labels first, then load company data
      this.getParentLabels();
    });
    
    this.companyForm.valueChanges.subscribe(() => {
      if (this.companyForm.dirty) {
        this.animationState = 'slide-up';
      }
    });
    
    // Set up country-specific controls
    this.companyForm.get('collectDirectly')?.valueChanges.subscribe(value => {
      if (value) {
        this.companyForm.get('collectorId')?.disable();
        this.companyForm.get('parentBoardId')?.disable();
      } else {
        this.companyForm.get('collectorId')?.enable();
        this.companyForm.get('parentBoardId')?.enable();
      }
    });

    // Watch for changes to collectDirectly to update validation
    this.companyForm.get('collectDirectly')?.valueChanges.subscribe(collectDirectly => {
      this.updateCollectorIdValidation(collectDirectly);
    });

    // Watch for changes to setParentLabel
    this.companyForm.get('setParentLabel')?.valueChanges.subscribe(setParentLabel => {
      const parentBoardIdControl = this.companyForm.get('parentBoardId');
      if (!setParentLabel) {
        this.companyForm.get('collectorId')?.disable();
        parentBoardIdControl?.disable();
        parentBoardIdControl?.clearValidators();
      } else {
        this.companyForm.get('collectorId')?.enable();
        parentBoardIdControl?.enable();
        parentBoardIdControl?.setValidators([Validators.required, this.parentLabelValidator.bind(this)]);
      }
      parentBoardIdControl?.updateValueAndValidity();
    });

    // Watch for changes to parentBoardId to automatically update collectorId
    this.companyForm.get('parentBoardId')?.valueChanges.subscribe(selectedName => {
      if (selectedName && this.parentLabels.length > 0) {
        const selectedParent = this.parentLabels.find(parent => 
          parent.rightsholderName === selectedName
        );
        if (selectedParent) {
          // Update collectorId with the company name
          this.companyForm.patchValue({ collectorId: selectedParent.rightsholderName }, { emitEvent: false });
        }
      } else if (!selectedName) {
        // Clear collectorId when parentBoardId is cleared
        this.companyForm.patchValue({ collectorId: '' }, { emitEvent: false });
      }
    });
  }

  loadData(): void {
    if (!this.boardId) return;
    
    // Check if this is a default board (MPO or MNN)
    this.isDefaultBoard = this.boardId === Boards.MPO || this.boardId === Boards.MNN;
    
    //this.spinnerService.show();
    this.companyService.getCompanybyBoardID(this.boardId).subscribe({
      next: (response) => {
        this.companyInfo = response;
        
        // Convert parentBoardId back to company name for display
        let displayCollectorName = '';
        let hasValidParent = false;
        
        // Check if parentBoardId is NOT one of the special boards
        if (this.companyInfo.parentBoardId && 
            this.companyInfo.parentBoardId !== Boards.MPO && 
            this.companyInfo.parentBoardId !== Boards.MNN) {
          hasValidParent = true;
          
          if (this.companyInfo.collectorId) {
            displayCollectorName = this.companyInfo.collectorId;
          } else if (this.parentLabels.length > 0) {
            // Fallback to finding by parentBoardId
            const parentLabel = this.parentLabels.find(parent => 
              parent.boardId === this.companyInfo.parentBoardId
            );
            displayCollectorName = parentLabel ? parentLabel.rightsholderName : '';
          }
        }
        
        // First patch the main form values
        this.companyForm.patchValue({
          memberType: this.companyInfo.memberType || 'Member',
          labelType: this.companyInfo.labelType || 'Label',
          isActive: this.companyInfo.isActive !== false,
          collectDirectly: this.companyInfo.collectDirectly !== false,
          setParentLabel: hasValidParent,
          collectorId: displayCollectorName,
          parentBoardId: displayCollectorName,
          useNemkonto: this.companyInfo.useNemkonto || false,
          dK_RegNo: this.companyInfo.dK_RegNo || '',
          dK_AccountNo: this.companyInfo.dK_AccountNo || '',
          nO_AccountNo: this.companyInfo.nO_AccountNo || '',
          intl_NameOfBank: this.companyInfo.intl_NameOfBank || '',
          intl_IBAN: this.companyInfo.intl_IBAN || '',
          intl_Swicfbic: this.companyInfo.intl_Swicfbic || '',
          ifpiDanmark: this.companyInfo.ifpiDanmark || false,
          ifpiNorge: this.companyInfo.ifpiNorge || false,
          fono: this.companyInfo.fono || false,
          companyAccessLevel: (this.companyInfo.companyAccessLevel ?? this.companyInfo.accessLevelId) === 2
        });
        
        // Update component properties
        this.Denmark = this.companyInfo.registeredCountry === 'Denmark';
        this.Norway = this.companyInfo.registeredCountry === 'Norway';
        
        // Mark as pristine after loading
        this.companyForm.markAsPristine();
        //this.spinnerService.hide();
      },
      error: (error) => {
        //this.spinnerService.hide();
        this.notificationService.showError('Company Not Exist please Fill Company Data');
      }
    });
  }

  getParentLabels() {

    //this.spinnerService.show();
    let userBoardId = this.currentUser.board?.id;
    
    if (!userBoardId) {
      //this.spinnerService.hide();
      return;
    }
    
    this.rightsholderBoardService.getParentRightsholders(this.organizationId!.toString())
      .subscribe({
        next: (res: any) => {
          this.parentLabels = res.data;
          //this.spinnerService.hide();
          // Load company data after parent labels are loaded
          this.loadData();
        },
        error: (error) => {
          //this.spinnerService.hide();
          console.error('Error loading parent labels:', error);
          // Still try to load data even if parent labels fail
          this.loadData();
        }
      });
  }

  parentLabelValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Empty value is valid (optional field)
    }
    
    const isValid = this.parentLabels.some(
      parent => parent.rightsholderName === control.value
    );
    
    return isValid ? null : { invalidParentLabel: true };
  }

  public cancel(): void {
    this.animationState = 'slide-down';
    setTimeout(() => {
      this.loadData();
      this.animationState = '';
    }, 200);
  }
   
  UpdateCompany() {
    // Mark all fields as touched to show validation errors
    this.markFormGroupTouched(this.companyForm);
    
    // Validate that if parent label is set, it must be valid
    const setParentLabel = this.companyForm.get('setParentLabel')?.value;
    const parentBoardIdValue = this.companyForm.get('parentBoardId')?.value;
    
    if (setParentLabel && !parentBoardIdValue) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a valid parent label or disable the parent label option.'
      });
      return;
    }
    
    if (this.companyForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the validation errors before saving.'
      });
      return;
    }

    // Get both collectorId (company name) and parentBoardId (GUID)
    let collectorId = null;
    let parentBoardId = null;
    
    if (this.companyForm.value.setParentLabel && this.companyForm.value.parentBoardId) {
      const selectedParent = this.parentLabels.find(parent => 
        parent.rightsholderName === this.companyForm.value.parentBoardId
      );
      if (selectedParent) {
        collectorId = this.companyForm.value.parentBoardId; 
        parentBoardId = selectedParent.boardId;
      }
    }

    const company: Company = {
      ...this.companyInfo,
      id: this.companyInfo.id,
      companyType: this.companyInfo.companyType,
      registeredCountry: this.companyInfo.registeredCountry,
      countryId: this.companyInfo.countryId,
      vatid: this.companyInfo.vatid,
      memberType: this.companyForm.value.memberType,
      labelType: this.companyForm.value.labelType,
      isActive: this.companyForm.value.isActive,
      collectDirectly: this.companyForm.value.collectDirectly,
      collectorId: collectorId, 
      parentBoardId: parentBoardId, 
      useNemkonto: this.companyForm.value.useNemkonto ?? false,
      dK_RegNo: this.companyForm.value.dK_RegNo || null,
      dK_AccountNo: this.companyForm.value.dK_AccountNo || null,
      nO_AccountNo: this.companyForm.value.nO_AccountNo || null,
      intl_NameOfBank: this.companyForm.value.intl_NameOfBank || null,
      intl_IBAN: this.companyForm.value.intl_IBAN || null,
      intl_Swicfbic: this.companyForm.value.intl_Swicfbic || null,
      ifpiDanmark: this.companyForm.value.ifpiDanmark ?? false,
      ifpiNorge: this.companyForm.value.ifpiNorge ?? false,
      fono: this.companyForm.value.fono ?? false,
      financialIdentifier: this.companyForm.value.financialIdentifier,
      companyAccessLevel: this.companyForm.value.companyAccessLevel ? 2 : 1,
      boardId: this.boardId
    };
    
    this.companyService.UpdateCompany(company).subscribe({
      next: (response) => {
        this.companyInfo = {
          ...this.companyInfo,
          ...response,
          id: company.id,
          companyType: company.companyType,
          registeredCountry: company.registeredCountry,
          countryId: company.countryId,
          vatid: company.vatid
        };
        this.companyForm.markAsPristine();
        //this.spinnerService.hide();
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Company updated successfully!'
        });
      },
      error: (error) => {
        //this.spinnerService.hide();
        this.notificationService.showError('Error updating company');
      }
    });
    
    this.animationState = 'slide-down';
    setTimeout(() => {
      this.animationState = '';
    }, 200);
  }

  private updateCollectorIdValidation(collectDirectly: boolean): void {
    const parentBoardIdControl = this.companyForm.get('parentBoardId');
    const setParentLabel = this.companyForm.get('setParentLabel')?.value;
    
    if (setParentLabel) {
      // When parent label is set, make parentBoardId required with validation
      parentBoardIdControl?.setValidators([Validators.required, this.parentLabelValidator.bind(this)]);
    } else {
      // When parent label is not set, remove validation
      parentBoardIdControl?.clearValidators();
    }
    
    parentBoardIdControl?.updateValueAndValidity();
  }

  toggleCollectDirectly(): void {
    const currentValue = this.companyForm.get('collectDirectly')?.value;
    this.companyForm.patchValue({ collectDirectly: !currentValue });
    
    // Update validation based on new value
    this.updateCollectorIdValidation(!currentValue);
    
    // Mark form as dirty and show save/cancel buttons
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleSetParentLabel(): void {
    // Prevent toggling for default boards (MPO and MNN)
    if (this.isDefaultBoard) {
      return;
    }
    
    const currentValue = this.companyForm.get('setParentLabel')?.value;
    this.companyForm.patchValue({ setParentLabel: !currentValue });
    
    if (!currentValue) {
      // When enabling parent label, do nothing else - allow collect directly to remain
    } else {
      // When disabling parent label, clear collectorId and parentBoardId, and enable collect directly
      this.companyForm.patchValue({ 
        collectorId: '', 
        parentBoardId: '',
        collectDirectly: true 
      });
    }
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleAccessLevel(): void {
    const currentValue = this.companyForm.get('companyAccessLevel')?.value;
    this.companyForm.patchValue({ companyAccessLevel: !currentValue });
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleIfpiNorge(): void {
    const currentValue = this.companyForm.get('ifpiNorge')?.value;
    this.companyForm.get('ifpiNorge')?.setValue(!currentValue);
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleFono(): void {
    const currentValue = this.companyForm.get('fono')?.value;
    this.companyForm.get('fono')?.setValue(!currentValue);
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleIfpiDanmark(): void {
    const currentValue = this.companyForm.get('ifpiDanmark')?.value;
    this.companyForm.get('ifpiDanmark')?.setValue(!currentValue);
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }

  toggleUseNemkonto(): void {
    const currentValue = this.companyForm.get('useNemkonto')?.value;
    this.companyForm.get('useNemkonto')?.setValue(!currentValue);
    
    this.companyForm.markAsDirty();
    this.animationState = 'slide-up';
  }
  
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Norwegian Account Number Formatting
  formatNorwegianAccountNumber(event: any): void {
    let input = event.target.value.replace(/\s/g, ''); 
    

    input = input.replace(/\D/g, '');
    

    if (input.length > 11) {
      input = input.substring(0, 11);
    }
    
    let formatted = '';
    if (input.length > 0) {
      formatted = input.substring(0, 4);
    }
    if (input.length > 4) {
      formatted += ' ' + input.substring(4, 6);
    }
    if (input.length > 6) {
      formatted += ' ' + input.substring(6, 11);
    }
    

    this.companyForm.patchValue({ nO_AccountNo: formatted }, { emitEvent: false });
  }
}
